import React, { Key, useMemo } from 'react';
import {
  DataTableProps,
  DataTableColumnsType,
  DataTableColumnType,
  DataTableRenderExpandedRow,
  DataTableRowsType,
  DataTableRowType,
} from './types';
import { TableRow } from './TableRow';
import { css } from '@emotion/react';
import { Checkbox } from '../checkbox';
import { Stack } from '../stack';
import { TableCell } from './TableCell';
import { getFixedMergeColumn } from './utils';
import { IconButton } from '../button';
import { motion } from 'framer-motion';
import { IconArrowChevronDown } from '../icons/generated';
import { semantic_colors } from '../../foundation';
import styled from '@emotion/styled';

const ArrowMotion = motion(Stack);

export interface DataTableRowProps<DataType extends object>
  extends Pick<
    DataTableProps<DataType>,
    'selectableRows' | 'clickableRows' | 'cellHoverStyle' | 'cellDisabledStyle' | 'resize'
  > {
  row: DataTableRowType<DataType>;
  columns: DataTableColumnsType<DataType>;
  index: number;
  rows: DataTableRowsType<DataType>;
  expandableButton: boolean;
  renderExpandedRow?: DataTableRenderExpandedRow<DataType>;
  dividerRows?: (row: DataType, index: number) => boolean;
  selected: boolean;
  expanded: boolean;
  rowColor?: string;
  isRowLast?: boolean;
  selectionKey?: Key;
  onSelectedRow?: (row: DataTableRowType<DataType>, index: number, selected: boolean) => void;
  onExpandedRow?: (row: DataTableRowType<DataType>, index: number, expanded: boolean) => void;
}

const DataTableRowImpl = <DataType extends object>({
  row,
  columns,
  index,
  rows,
  selected,
  expanded,
  rowColor,
  isRowLast,
  selectionKey,
  onSelectedRow,
  onExpandedRow,
  selectableRows,
  expandableButton,
  renderExpandedRow,
  dividerRows,
  clickableRows,
  cellHoverStyle,
  cellDisabledStyle,
  resize,
}: DataTableRowProps<DataType>) => {
  const hasSelectableRows = !!selectableRows;
  const expandedRow = renderExpandedRow && renderExpandedRow(row as DataType, index);
  const renderedColumns = useMemo(
    () =>
      columns.map((column, flattenIndex) => {
        const baseColumn = column as DataTableColumnType<DataType>;
        const { id, cellAttributes, wrapper = true, ...rest } = baseColumn;
        const align = baseColumn.cellAlign ?? baseColumn.align ?? 'left';
        const cellAttributesProps = cellAttributes && cellAttributes(row, index, rows);
        const { style, disabled: cellDisabled, ...spanProps } = cellAttributesProps || {};
        const tableCellProps = {
          resize,
          ...spanProps,
          cellDisabledStyle,
          selectable: !row.disabled && (row.selectable ?? true),
          expandable: !row.disabled && (row.expandable ?? true),
          clickable: clickableRows,
          ...rest,
          width: undefined,
          ...(rest.fixed && {
            fixedColWidth: getFixedMergeColumn(
              columns,
              hasSelectableRows,
              flattenIndex,
              !!renderExpandedRow && expandableButton,
            ),
          }),
          disabled: cellDisabled !== undefined ? cellDisabled : cellDisabledStyle && row.disabled,
          align,
        };
        const render_cell = (
          baseColumn.cell ? baseColumn.cell({ row, value: row[id], index, rows }) : row[id]
        ) as React.ReactNode;
        return (
          <TableCell
            className={`pds-table-cell-${id}`}
            key={flattenIndex}
            {...tableCellProps}
            css={style}
            isRowLast={
              Boolean(
                tableCellProps.rowSpan && tableCellProps.rowSpan > 1 && index + tableCellProps.rowSpan === rows.length,
              ) || isRowLast
            }
            isColumnLast={Boolean(
              tableCellProps.colSpan &&
                tableCellProps.colSpan > 1 &&
                flattenIndex + tableCellProps.colSpan === columns.length,
            )}
          >
            {wrapper ? (
              <Stack
                direction='row'
                align='center'
                style={{
                  justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'initial',
                  wordWrap: 'break-word',
                  wordBreak: 'break-word',
                }}
              >
                {resize ? <span className='ContentsWrapper'>{render_cell}</span> : render_cell}
              </Stack>
            ) : resize ? (
              <span className='ContentsWrapper'>{render_cell}</span>
            ) : (
              render_cell
            )}
          </TableCell>
        );
      }),
    [
      columns,
      index,
      rows,
      cellDisabledStyle,
      row.disabled,
      row.selectable,
      clickableRows,
      hasSelectableRows,
      row.expandable,
    ],
  );

  const checkboxCell = useMemo(() => {
    if (!selectableRows) {
      return null;
    }
    const selectableRowsObject = typeof selectableRows === 'object' ? selectableRows : {};
    const cellAttributesProps = selectableRowsObject.cellAttributes?.(row, index, rows);
    const { style, ...spanProps } = cellAttributesProps || {};
    const checkboxCellProps = {
      selectable: !row.disabled && (row.selectable ?? true),
      clickable: clickableRows,
      ...(columns[0]?.fixed && { fixed: true, fixedColWidth: 0 }),
      ...spanProps,
    };
    return (
      <TableCell
        {...checkboxCellProps}
        css={style}
        isRowLast={
          Boolean(
            checkboxCellProps.rowSpan &&
              checkboxCellProps.rowSpan > 1 &&
              index + checkboxCellProps.rowSpan === rows.length,
          ) || isRowLast
        }
      >
        <Stack direction='column' align='center'>
          <Checkbox
            name={`row-${selectionKey}-select`}
            disabled={row.disabled || !(row.selectable ?? true) || !selectionKey}
            checked={selected}
            aria-checked={selected}
            onChange={clickableRows ? undefined : () => onSelectedRow?.(row, index, selected)}
            readOnly={clickableRows ? true : undefined}
            onClick={(e) => e.stopPropagation()}
            css={css`
              justify-content: center;
            `}
          />
        </Stack>
      </TableCell>
    );
  }, [selectableRows, row, index, rows, clickableRows, columns, isRowLast, selectionKey, selected, onSelectedRow]);

  const expandCell = useMemo(() => {
    if (!renderExpandedRow || !expandableButton) {
      return null;
    }

    const is_disabled = row.disabled || !(row.expandable ?? true);

    const expandCellProps = {
      expandable: !is_disabled,
      ...(columns[0]?.fixed && { fixed: true, fixedColWidth: selectableRows ? 48 : 0 }),
    };

    return (
      <TableCell {...expandCellProps}>
        <Stack direction='column' align='center'>
          <IconButton
            kind={expanded ? 'outlined_primary' : 'outlined_secondary'}
            disabled={is_disabled || !selectionKey}
            size='xsmall'
            onClick={() => onExpandedRow?.(row, index, expanded)}
            icon={
              <ArrowMotion
                animate={{
                  rotate: expanded ? -180 : 0,
                }}
                transition={{ duration: 0.25 }}
              >
                <IconArrowChevronDown
                  color={
                    is_disabled
                      ? semantic_colors.content.secondary
                      : expanded
                      ? semantic_colors.accent.primary
                      : semantic_colors.content.primary
                  }
                />
              </ArrowMotion>
            }
          />
        </Stack>
      </TableCell>
    );
  }, [renderExpandedRow, expandableButton, row, index, columns, selectableRows, expanded, selectionKey, onExpandedRow]);

  return (
    <>
      <TableRow
        selected={selected}
        rowColor={rowColor}
        cellHoverStyle={cellHoverStyle}
        onClick={!row.disabled && clickableRows ? () => onSelectedRow?.(row, index, selected) : undefined}
        selectable={!row.disabled && (row.selectable ?? true)}
        disabled={row.disabled}
        clickable={clickableRows}
      >
        {checkboxCell}
        {expandableButton && expandCell}
        {renderedColumns}
      </TableRow>
      {expandedRow && (!expandableButton || expanded) && (
        <TableRow>
          <TableCell
            className='pds-table-cell pds-table-expanded-cell'
            colSpan={columns.length + (selectableRows ? 1 : 0) + (expandableButton ? 1 : 0)}
          >
            <ExpandableRowContainer>{expandedRow}</ExpandableRowContainer>
          </TableCell>
        </TableRow>
      )}
      {rows.length - 1 !== index && dividerRows?.(row as DataType, index) && (
        <TableRow>
          <TableCell
            className='pds-table-cell pds-table-divider-cell'
            colSpan={columns.length + (selectableRows ? 1 : 0) + (!!renderExpandedRow && expandableButton ? 1 : 0)}
          />
        </TableRow>
      )}
    </>
  );
};

export const DataTableRow = React.memo(DataTableRowImpl) as typeof DataTableRowImpl;

const ExpandableRowContainer = styled.div`
  position: relative;
  width: 100%;
  z-index: 0;
`;
